import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useTranslate } from '@translations/useTranslate';
import { IFormItemType } from '@interfaces/FormItemType';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import { CustomButton, FormContainer } from '@ui/components/atoms';
import ArrowIcon from '@assets/svg/arrow.svg';
import ReCAPTCHA from 'react-google-recaptcha';
import { ISurveyForm } from '@interfaces/Survey.interface';
import { IMappedSurveyResponse } from '@interfaces/SurveyLookups.interface';
import { SurveyFormValidationScheme } from '@ui/components/organisms/SurveyForm/SurveyForm.scheme';
import { useSubmitSurveyForm } from '@services/Survey/hooks/useSubmitSurveyForm';
import { getOptionByKey } from '@utils/helper.util';
import SurveyFormStyles from './SurveyForm.module.scss';

const RECAPTCHA_SITE_KEY = '6LdRwy4qAAAAAIN7oQMsZevTuDmW2m302maO2hwf';

export const SurveyForm: FC<{
  lookups: IMappedSurveyResponse;
  onRequestSuccess: (successRequestId: string) => void;
  requestId: string;
}> = ({ lookups, onRequestSuccess, requestId }) => {
  const { t } = useTranslate();
  const history = useHistory();
  const [error, setError] = React.useState<string | undefined>();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const { mutateAsync: submitRequest, isLoading } = useSubmitSurveyForm();

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaToken('');
  };

  const submit = useCallback(
    (values) => {
      if (!recaptchaToken) {
        return;
      }
      setError('');
      const removedEmptyValues: ISurveyForm = Object.fromEntries(
        Object.entries(values).map(([key, value]) => {
          return [key, value === '' ? undefined : value];
        })
      ) as unknown as ISurveyForm;

      const mappedValues: ISurveyForm = {
        ...removedEmptyValues,
        satisfactionLevelId:
          getOptionByKey(removedEmptyValues.satisfactionLevelId, lookups.SATISFACTION_LEVEL)?.id ??
          removedEmptyValues.satisfactionLevelId,
        easeOfUseId:
          getOptionByKey(removedEmptyValues.easeOfUseId, lookups.EASE_OF_USE)?.id ??
          removedEmptyValues.easeOfUseId,
        anyIssueId:
          getOptionByKey(removedEmptyValues.anyIssueId, lookups.ANY_ISSUE)?.id ??
          removedEmptyValues.anyIssueId,
      };

      const formData = new FormData();
      Object.entries(mappedValues).forEach(([key, value]) => {
        if (value) {
          formData.append(key, value ?? '');
        }
      });
      submitRequest(
        { requestId: requestId, requestData: formData, recaptchaToken },
        {
          onError: (error) => {
            const errorData = error?.response?.data as { errorMessage?: string };
            setError(errorData?.errorMessage ?? t('global.error'));
          },
        }
      )
        .then((id) => {
          onRequestSuccess(id);
          //navigate to next page
        })
        .catch(() => {
          //Ignore the exception
        })
        .finally(() => {
          // single-use token; clear it so a stale token isn't reused
          resetRecaptcha();
        });
    },
    [lookups, onRequestSuccess, requestId, submitRequest, t, recaptchaToken]
  );

  const surveyFormScheme = useMemo(() => SurveyFormValidationScheme(t), [t]);
  const formik = useFormik<ISurveyForm>({
    initialValues: {
      satisfactionLevelId: '',
      easeOfUseId: '',
      anyIssueId: '',
      issueDescription: '',
      comments: '',
    },
    onSubmit: async (values) => {
      submit(values);
    },
    validationSchema: surveyFormScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });
  const SatisfactionItems: IFormItemType[] = useMemo(() => {
    const items: IFormItemType[] = [
      {
        id: 'satisfactionLevelId',
        label: t('survey.form.satisfactionLevelId.label'),
        placeholder: '',
        type: 'radio',
        required: true,
        isValid: true,
        value: formik.values.satisfactionLevelId,
        isTouched: formik.touched.satisfactionLevelId,
        errorFeedback: formik.errors.satisfactionLevelId,
        onChange: (value) => {
          formik.setFieldValue('satisfactionLevelId', value);
        },
        onBlur: () => formik.setFieldTouched('satisfactionLevelId', true),
        RadioOptions: lookups.SATISFACTION_LEVEL,
      },
    ];
    return items;
  }, [formik, lookups]);
  const EasOfUseItems: IFormItemType[] = useMemo(() => {
    const items: IFormItemType[] = [
      {
        id: 'easeOfUseId',
        label: t('survey.form.easeOfUseId.label'),
        placeholder: '',
        type: 'radio',
        required: true,
        isValid: true,
        value: formik.values.easeOfUseId,
        isTouched: formik.touched.easeOfUseId,
        errorFeedback: formik.errors.easeOfUseId,
        onChange: (value) => {
          formik.setFieldValue('easeOfUseId', value);
        },
        onBlur: () => formik.setFieldTouched('easeOfUseId', true),
        RadioOptions: lookups.EASE_OF_USE,
      },
    ];
    return items;
  }, [formik, lookups]);
  const IssuesItems: IFormItemType[] = useMemo(() => {
    const items: IFormItemType[] = [
      {
        id: 'anyIssueId',
        label: t('survey.form.anyIssueId.label'),
        placeholder: '',
        type: 'radio',
        required: true,
        isValid: !formik.errors.issueDescription,
        value: formik.values.anyIssueId,
        isTouched: formik.touched.anyIssueId,
        errorFeedback: formik.errors.anyIssueId,
        onChange: (value) => {
          formik.setFieldValue('anyIssueId', value);
          if (value === 'NO') {
            formik.setFieldValue('issueDescription', '');
          }
          setTimeout(() => {
            formik.validateField('issueDescription');
          }, 0);
        },
        onBlur: () => formik.setFieldTouched('anyIssueId', true),
        RadioOptions: lookups.ANY_ISSUE,
      },
    ];

    if (formik.values.anyIssueId === 'YES') {
      items.push({
        id: 'issueDescription',
        label: t('survey.form.issueDescription.label'),
        placeholder: t('survey.form.issueDescription.placeholder'),
        inline: false,
        type: 'textarea',
        required: true,
        isValid: formik.isValid,
        maxLength: 500,
        value: formik.values.issueDescription,
        isTouched: formik.touched.issueDescription,
        errorFeedback: formik.errors.issueDescription,
        onChange: (value) => {
          formik.setFieldValue('issueDescription', value);
        },
        onBlur: () => formik.setFieldTouched('issueDescription', true),
      });
    }
    items.push({
      id: 'comments',
      label: t('survey.form.comments.label'),
      placeholder: t('survey.form.comments.placeholder'),
      inline: false,
      type: 'textarea',
      required: false,
      isValid: formik.isValid,
      maxLength: 500,
      value: formik.values.comments,
      isTouched: formik.touched.comments,
      errorFeedback: formik.errors.comments,
      onChange: (value) => {
        formik.setFieldValue('comments', value);
      },
      onBlur: () => formik.setFieldTouched('comments', true),
    });

    return items;
  }, [formik]);

  return (
    <FormContainer error={error}>
      <SubFormSection items={SatisfactionItems} />
      <SubFormSection items={EasOfUseItems} />
      <SubFormSection items={IssuesItems} />

      <div className={SurveyFormStyles.recaptcha_wrapper}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={(token) => setRecaptchaToken(token ?? '')}
          onExpired={() => setRecaptchaToken('')}
          onErrored={() => setRecaptchaToken('')}
        />
      </div>

      <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
        <CustomButton
          variant="primary"
          text={t('submit')}
          disabled={isLoading || !recaptchaToken}
          suffixIcon={ArrowIcon}
          onClick={() => {
            formik.handleSubmit();
          }}
        />
        <CustomButton
          variant="outline-dark"
          text={t('cancel')}
          onClick={() => history.go(-1)}
          disabled={false}
          className="mx-3"
        />
      </div>
    </FormContainer>
  );
};
